import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";

import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

import CoverLayout from "layouts/authentication/components/CoverLayout";
import { useAuth } from "contexts/AuthContext";

const bgImage =
  "https://images.unsplash.com/photo-1497366811353-6870744d04b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80";

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CoverLayout
      title="Welcome Back"
      description="Sign in to your PRM Core account"
      image={bgImage}
      imgPosition="top"
      button={{ color: "dark", variant: "gradient" }}
    >
      <Card>
        <ArgonBox pt={3} pb={1} px={3} textAlign="center">
          <ArgonTypography variant="h5" fontWeight="bold">
            Sign In
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
            Manage more. Click less.
          </ArgonTypography>
        </ArgonBox>

        <ArgonBox pt={2} pb={3} px={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <ArgonBox component="form" role="form" onSubmit={handleSubmit}>
            {/* Email */}
            <ArgonBox mb={2}>
              <ArgonTypography
                variant="caption"
                fontWeight="bold"
                color="text"
                sx={{ mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Email
              </ArgonTypography>
              <ArgonInput
                type="email"
                placeholder="you@company.com"
                size="large"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </ArgonBox>

            {/* Password */}
            <ArgonBox mb={3}>
              <ArgonTypography
                variant="caption"
                fontWeight="bold"
                color="text"
                sx={{ mb: 0.5, display: "block", textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Password
              </ArgonTypography>
              <ArgonInput
                type="password"
                placeholder="Enter your password"
                size="large"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </ArgonBox>

            {/* Submit */}
            <ArgonBox mb={2}>
              <ArgonButton
                type="submit"
                variant="gradient"
                color="dark"
                size="large"
                fullWidth
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </ArgonButton>
            </ArgonBox>

            {/* Request Access Link */}
            <ArgonBox textAlign="center" mt={3}>
              <ArgonTypography variant="button" color="text" fontWeight="regular">
                Need an account?&nbsp;
                <ArgonTypography
                  component="a"
                  href="https://prmcore.com"
                  variant="button"
                  color="dark"
                  fontWeight="bold"
                  textGradient
                >
                  Request Access
                </ArgonTypography>
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </Card>
    </CoverLayout>
  );
}

export default SignIn;
